import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`In your React Native or Expo project add the `}<inlineCode parentName="p">{`posthog-react-native`}</inlineCode>{` package to your project as well as the required peer dependencies.`}</p>
    <h4 {...{
      "id": "expo-apps"
    }}>{`Expo apps`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-sh"
      }}>{`expo install posthog-react-native expo-file-system expo-application expo-device expo-localization
`}</code></pre>
    <h4 {...{
      "id": "react-native-apps"
    }}>{`React Native apps`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-sh"
      }}>{`yarn add posthog-react-native @react-native-async-storage/async-storage react-native-device-info
# or
npm i -s posthog-react-native @react-native-async-storage/async-storage react-native-device-info
`}</code></pre>
    <h3 {...{
      "id": "configuration"
    }}>{`Configuration`}</h3>
    <h4 {...{
      "id": "with-the-posthogprovider"
    }}>{`With the PosthogProvider`}</h4>
    <p>{`The recommended way to set up PostHog for React Native is to use the `}<inlineCode parentName="p">{`PostHogProvider`}</inlineCode>{` which utilizes the Context API to pass the PostHog client around, enable autocapture, and ensure that the queue is flushed at the right time:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react-native"
      }}>{`// App.(js|ts)
import { usePostHog, PostHogProvider } from 'posthog-react-native'
...

export function MyApp() {
    return (
        <PostHogProvider apiKey="<ph_project_api_key>" options={{
            host: '<ph_instance_address>', // TIP: This is https://app.posthog.com by default
        }}>
            <MyComponent />
        </PostHogProvider>
    )
}

// Now you can simply access PostHog elsewhere in the app like so:

const MyComponent = () => {
    const posthog = usePostHog()

    useEffect(() => {
        posthog.capture("MyComponent loaded", { foo: "bar" })
    }, [])
}
`}</code></pre>
    <h3 {...{
      "id": "configuration-1"
    }}>{`Configuration`}</h3>
    <h4 {...{
      "id": "without-the-posthogprovider"
    }}>{`Without the PosthogProvider`}</h4>
    <p>{`Due to the async nature of React Native, PostHog needs to be initialized asynchronously for the persisted state to load properly. The `}<inlineCode parentName="p">{`PosthogProvider`}</inlineCode>{` takes care of this under-the-hood but you can alternatively create the instance yourself like so:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-react-native"
      }}>{`// posthog.ts
import PostHog from 'posthog-react-native'

export let posthog: PostHog | undefined = undefined

export const posthogAsync: Promise<PostHog> = PostHog.initAsync('<ph_project_api_key>', {
  host: '<ph_instance_address>' // TIP: This is https://app.posthog.com by default
})

posthogAsync.then(client => {
  posthog = client
})


// app.ts
import { posthog, posthogAsync} from './posthog'

export function MyApp1() {
    useEffect(async () => {
        // Use posthog optionally with the possibility that it may still be loading
        posthog?.capture('MyApp1 loaded')
        // OR use posthog via the promise
        (await posthogAsync).capture('MyApp1 loaded')
    }, [])

    return <View>Your app code</View>
}


// You can even use this instance with the PostHogProvider
export function MyApp2() {
  return <PostHogProvider client={posthogAsync}>{/* Your app code */}</PostHogProvider>
}
`}</code></pre>
    <h3 {...{
      "id": "configuration-options"
    }}>{`Configuration options`}</h3>
    <p>{`You can further customize how PostHog works through its configuration on initialization.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ts"
      }}>{`export const posthog = await PostHog.initAsync("<ph_project_api_key>", {
    // PostHog API host
    host?: string = "https://app.posthog.com",
    // The number of events to queue before sending to PostHog (flushing)
    flushAt?: number = 20,
    // The interval in milliseconds between periodic flushes
    flushInterval?: number = 10000
    // If set to false, tracking will be disabled until \`optIn\` is called
    enable?: boolean = true,
    // Whether to track that \`getFeatureFlag\` was called (used by Expriements)
    sendFeatureFlagEvent?: boolean = true,
    // Whether to load feature flags when initialised or not
    preloadFeatureFlags?: boolean = true,
    // How many times we will retry HTTP requests
    fetchRetryCount?: number = 3,
    // The delay between HTTP request retries
    fetchRetryDelay?: number = 3000,
    // For Session Analysis how long before we expire a session
    sessionExpirationTimeSeconds?: number = 1800 // 30 mins
    // Whether to post events to PostHog in JSON or compressed format
    captureMode?: 'json' | 'form'
})
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      